import { PropsWithChildren } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import { customImageLoader, formatDateToDayMonthYear, cn } from '@restworld/utils-common';
import { Badge, Heading } from '@restworld/ui-ds-base';
import { Alert } from './alert';
import { LinkBase } from '@restworld/ui-components';
import { MapPinIcon, GlobeAltIcon, CalendarIcon } from '@heroicons/react/24/solid';

export function WorkerBaseCard({ variant, badges, city, firstSubDate, logoSrc, venueName }: WorkerBaseCardProps) {
  return (
    <CardWrapper variant={variant}>
      <CardJpDetails badges={badges} />
      <CardVenueDetails
        variant={variant}
        logoSrc={logoSrc}
        city={city}
        firstSubDate={firstSubDate}
        venueName={venueName}
      />
      <CardSubscriptionDetails firstSubDate={firstSubDate} />
    </CardWrapper>
  );
}
export default WorkerBaseCard;

export function CardWrapper({ children, variant, newTab, onClick, wrapperStyle, href = '#!' }: CardWrapperProps) {
  return (
    <Wrapper link={href} variant={variant} newTab={newTab} onLinkClick={onClick} twStyle={wrapperStyle as string}>
      {children}
    </Wrapper>
  );
}

export function CardJpDetails({ badges, title }: CardJpDetailsProps) {
  return (
    <div>
      <CardTitle content={title} />
      <div className="mt-2 flex gap-2 items-center flex-wrap max-w-[288px]">
        {!!badges?.length &&
          badges.map((badge) => (
            <Badge key={badge} variant="fresh" className="w-fit">
              {badge}
            </Badge>
          ))}
      </div>
    </div>
  );
}

export function CardVenueDetails({
  logoSrc,
  variant,
  city,
  firstSubDate,
  venueName,
  isOutsideItaly,
  hasAlreadyApplied,
}: CardVenueDetailsProps) {
  const { t } = useTranslation();
  const isJpVariant = variant === 'jp';
  const isVenueVariant = variant === 'venue';

  return (
    <div>
      <div className="flex items-center gap-3">
        {logoSrc && (
          <div className="h-[43px] w-[43px] rounded-lg border border-neutral-90 relative overflow-hidden">
            <Image alt="" loader={customImageLoader} fill src={logoSrc} />
          </div>
        )}
        <div>
          {isJpVariant && (
            <Heading as="span" size={6} weight="medium" color="neutral" className="!text-sm">
              {venueName}
            </Heading>
          )}
          <div className="flex gap-3">
            {city && (
              <span className="flex items-center gap-1 !text-sm font-medium text-neutral-700 break-all">
                {isOutsideItaly ? (
                  <GlobeAltIcon className="h-4 w-4 shrink-0" />
                ) : (
                  <MapPinIcon className="h-4 w-4 shrink-0" />
                )}
                <Heading
                  as="span"
                  size={6}
                  weight="medium"
                  color="neutral"
                  className="!text-sm break-all"
                  maxChars={logoSrc ? 28 : 33}
                >
                  {city}
                </Heading>
              </span>
            )}
            {isJpVariant && firstSubDate && (
              <span className="flex items-center gap-1 !text-sm font-medium text-neutral-700">
                <CalendarIcon className="h-4 w-4 shrink-0" />
                <Heading as="span" size={6} weight="medium" color="neutral" className="!text-sm">
                  {formatDateToDayMonthYear(firstSubDate)}
                </Heading>
              </span>
            )}
          </div>
        </div>
      </div>
      {isJpVariant && hasAlreadyApplied && (
        <Alert variant="success" text={`${t('common:you_applied')}*`} wrapperStyling={'mt-2'} />
      )}
      {isVenueVariant && <CardTitle content={venueName} twStyle={'mt-2'} />}
    </div>
  );
}

export function CardSubscriptionDetails({ firstSubDate }: CardSubscriptionDetailsProps) {
  const { t } = useTranslation('common');

  if (!firstSubDate) return null;

  return (
    <Heading as="span" size={6} weight="medium" color="neutral" className="!text-sm">
      {t('subscribed_since_date', { date: formatDateToDayMonthYear(firstSubDate) })}
    </Heading>
  );
}

function CardTitle({ content, twStyle }: { content?: string; twStyle?: string }) {
  return (
    <Heading as="span" size={5} weight="medium" className={cn('!text-lg max-w-[288px]', twStyle)} maxChars={78}>
      {content}
    </Heading>
  );
}

export interface WorkerBaseCardProps {
  variant: WorkerBaseCardVariant;
  badges?: string[];
  logoSrc?: string;
  city?: string;
  firstSubDate?: string;
  venueName?: string;
}
export type WorkerBaseCardVariant = 'jp' | 'venue';
interface CardWrapperProps extends PropsWithChildren {
  variant: WorkerBaseCardVariant;
  href?: string;
  newTab?: boolean;
  onClick?: () => void;
  wrapperStyle?: string;
}
interface CardJpDetailsProps {
  badges?: string[];
  title?: string;
}
interface CardVenueDetailsProps {
  logoSrc?: string;
  variant: WorkerBaseCardVariant;
  city?: string;
  firstSubDate?: string;
  venueName?: string;
  isOutsideItaly?: boolean;
  hasAlreadyApplied?: boolean;
}
interface CardSubscriptionDetailsProps {
  firstSubDate?: string;
}

const Wrapper = ({
  variant,
  twStyle,
  className,
  ...props
}: { variant?: WorkerBaseCardVariant; twStyle?: string } & React.ComponentProps<typeof LinkBase>) => (
  <LinkBase
    className={cn(
      'w-fit min-w-[320px] max-w-[640px] p-4 gap-8 rounded-2xl border transition-all flex flex-col',
      variant === 'jp' &&
        "bg-purple-500/10 outline-purple-500 border-purple-500 hover:bg-purple-500/15 hover:shadow-[inset_0_0_0_1px_theme('colors.purple.500')]",
      variant === 'venue' &&
        "bg-neutral-900/5 outline-neutral-90 border-neutral-90 hover:bg-neutral-900/15 hover:shadow-[inset_0_0_0_1px_theme('colors.neutral.90')]",
      twStyle,
      className
    )}
    {...props}
  />
);
